import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ScanLine, Upload, Trash2, Download, Image } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyScans = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [scanType, setScanType] = useState('xray');

  useEffect(() => { fetchScans(); }, []);
  const fetchScans = async () => {
    try { const res = await axios.get(`${API}/scans/${user.id}`, { headers: { Authorization: `Bearer ${token}` } }); setScans(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file); formData.append('patient_id', user.id); formData.append('description', description); formData.append('scan_type', scanType);
      await axios.post(`${API}/scans/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success(t.upload + ' OK!'); setDescription(''); fetchScans();
    } catch (err) { toast.error('Failed'); } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API}/scans/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('OK'); setScans(scans.filter(s => s.id !== id)); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="my-scans-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.myScans}</h1>
        <p className="text-[#6B7280]">{t.myScansSubtitle}</p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.uploadNewScan}</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <select value={scanType} onChange={e => setScanType(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" data-testid="scan-type-select">
            <option value="xray">{t.xray}</option><option value="panoramic">{t.panoramic}</option><option value="cbct">{t.cbct}</option><option value="other">{t.other}</option>
          </select>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder={t.descriptionOptional} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20" data-testid="scan-description" />
          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#0F4C5C]/30 rounded-lg cursor-pointer hover:bg-[#0F4C5C]/5 transition-colors" data-testid="scan-upload-btn">
            <Upload className="w-5 h-5 text-[#0F4C5C]" /><span className="text-sm font-medium text-[#0F4C5C]">{uploading ? t.uploading : t.clickToUploadScan}</span>
            <input type="file" accept="image/*,.pdf,.dcm" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
      </div>

      {scans.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center"><ScanLine className="w-12 h-12 text-[#6B7280] mx-auto mb-4" /><p className="text-[#6B7280]">{t.noScansYet}</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {scans.map(scan => (
            <div key={scan.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={`scan-card-${scan.id}`}>
              <div className="h-40 bg-slate-100 flex items-center justify-center overflow-hidden">
                {scan.filename && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(scan.filename) ? (
                  <img src={`${API}/scans/file/${scan.id}?token=${token}`} alt={scan.description || scan.filename} className="w-full h-full object-cover" />
                ) : (<Image className="w-12 h-12 text-[#6B7280]" />)}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="px-2 py-0.5 bg-[#0F4C5C]/10 text-[#0F4C5C] rounded text-xs font-semibold">{(scan.scan_type || 'xray').toUpperCase()}</span>
                  <span className="text-xs text-[#6B7280]">{new Date(scan.uploaded_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm font-medium text-[#1F2937] truncate">{scan.filename}</p>
                {scan.description && <p className="text-xs text-[#6B7280] mt-1 truncate">{scan.description}</p>}
                <div className="flex gap-2 mt-3">
                  <a href={`${API}/scans/file/${scan.id}?token=${token}`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-[#0F4C5C] text-white rounded-lg text-sm hover:bg-[#0F4C5C]/90 transition-colors" data-testid={`download-scan-${scan.id}`}><Download className="w-3.5 h-3.5" /> {t.view}</a>
                  <button onClick={() => handleDelete(scan.id)} className="px-3 py-1.5 text-red-500 border border-red-200 rounded-lg text-sm hover:bg-red-50 transition-colors" data-testid={`delete-scan-${scan.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyScans;
